import React, { useState, useEffect } from 'react';
import {
  CForm,
  CRow,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CButton,
  CSpinner,
  CAlert,
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const UsersInputs = ({ 
  onSubmit, 
  onCancel, 
  isLoading = false, 
  submitResponse = null,
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    name: '',
    surname: '',
    cepTel: '',
    telefon: '',
    status: 1
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  // Status options - API'de 1 = active, 0 = inactive
  const statusOptions = [
    { value: 1, label: t('activeUser') || 'Aktif' },
    { value: 0, label: t('inactiveUser') || 'İnaktif' }
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        userName: initialData.UserName || '',
        email: initialData.Email || '',
        name: initialData.Name || '',
        surname: initialData.Surname || '',
        cepTel: initialData.CepTel || '',
        telefon: initialData.Telefon || '',
        status: initialData.Status ?? 1
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            userName: '',
            email: '',
            name: '',
            surname: '',
            cepTel: '',
            telefon: '',
            status: 1
          });
        }
        setShowSuccess(false);
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    const finalValue = name === 'status' ? parseInt(value, 10) : value;
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10,11}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.userName.trim()) {
      newErrors.userName = t('userNameRequired') || 'Kullanıcı adı gereklidir';
    }
    
    // Removed mandatory validation - fields are now optional
    // Only validate email format if email is provided
    if (formData.email.trim() && !validateEmail(formData.email)) {
      newErrors.email = t('invalidEmailFormat') || 'Geçersiz e-posta formatı';
    }
    
    // Only validate phone format if phone numbers are provided
    if (formData.cepTel.trim() && !validatePhoneNumber(formData.cepTel)) {
      newErrors.cepTel = t('invalidPhoneFormat') || 'Geçersiz telefon formatı';
    }
    if (formData.telefon.trim() && !validatePhoneNumber(formData.telefon)) {
      newErrors.telefon = t('invalidPhoneFormat') || 'Geçersiz telefon formatı';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (!userId || userId === 'undefined') {
        setErrors({ general: t('userSessionNotFound') || 'Kullanıcı oturumu bulunamadı' });
        return;
      }

      const submitData = {
        SessionUserId: String(userId),
        UserName: formData.userName.trim(),
        Email: formData.email.trim(),
        Name: formData.name.trim(),
        Surname: formData.surname.trim(),
        CepTel: formData.cepTel.trim(),
        Telefon: formData.telefon.trim(),
        Status: isEdit ? formData.status : 1
      };

      if (isEdit && initialData?.UserId) {
        submitData.UserId = initialData.UserId;
      }

      console.log(`Submitting ${isEdit ? 'update' : 'create'} user data:`, submitData);
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        userName: '',
        email: '',
        name: '',
        surname: '',
        cepTel: '',
        telefon: '',
        status: 1
      });
    }
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    return isEdit 
      ? (t('userUpdated') || 'Kullanıcı güncellendi') 
      : (t('userCreated') || 'Kullanıcı oluşturuldu');
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit 
        ? (t('updating') || 'Güncelleniyor...') 
        : (t('creating') || 'Oluşturuluyor...');
    }
    return isEdit 
      ? (t('updateUser') || 'Kullanıcıyı Güncelle') 
      : (t('createUser') || 'Kullanıcı Oluştur');
  };

  return (
    <div>
      {errors.general && (
        <CAlert color="danger" 
        dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="userName">
              {t('userName') || 'Kullanıcı Adı'}
            </CFormLabel>
            <CFormInput
              type="text"
              id="userName"
              name="userName"
              value={formData.userName}
              onChange={handleInputChange}
              invalid={!!errors.userName}
              placeholder={t('enterUserName') || 'Kullanıcı adını girin'}
              disabled={isLoading}
            />
            {errors.userName && (
              <div className="invalid-feedback d-block">
                {errors.userName}
              </div>
            )}
          </CCol>
          
          <CCol md={6}>
            <CFormLabel htmlFor="email">
              {t('userEmail') || 'E-posta'}
            </CFormLabel>
            <CFormInput
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              invalid={!!errors.email}
              placeholder={t('enterUserEmail') || 'E-posta adresini girin'}
              disabled={isLoading}
            />
            {errors.email && (
              <div className="invalid-feedback d-block">
                {errors.email}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="name">
              {t('name') || 'Ad'}
            </CFormLabel>
            <CFormInput
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              invalid={!!errors.name}
              placeholder={t('enterName') || 'Adı girin'}
              disabled={isLoading}
            />
            {errors.name && (
              <div className="invalid-feedback d-block">
                {errors.name}
              </div>
            )}
          </CCol>
          
          <CCol md={6}>
            <CFormLabel htmlFor="surname">
              {t('surname') || 'Soyad'}
            </CFormLabel>
            <CFormInput
              type="text"
              id="surname"
              name="surname"
              value={formData.surname}
              onChange={handleInputChange}
              invalid={!!errors.surname}
              placeholder={t('enterSurname') || 'Soyadı girin'}
              disabled={isLoading}
            />
            {errors.surname && (
              <div className="invalid-feedback d-block">
                {errors.surname}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="cepTel">
              {t('mobilePhone') || 'Cep Telefonu'}
            </CFormLabel>
            <CFormInput
              type="tel"
              id="cepTel"
              name="cepTel"
              value={formData.cepTel}
              onChange={handleInputChange}
              invalid={!!errors.cepTel}
              placeholder={t('enterMobilePhone') || 'Cep telefonu girin'}
              disabled={isLoading}
            />
            {errors.cepTel && (
              <div className="invalid-feedback d-block">
                {errors.cepTel}
              </div>
            )}
          </CCol>
          
          <CCol md={6}>
            <CFormLabel htmlFor="telefon">
              {t('userPhone') || 'Telefon'}
            </CFormLabel>
            <CFormInput
              type="tel"
              id="telefon"
              name="telefon"
              value={formData.telefon}
              onChange={handleInputChange}
              invalid={!!errors.telefon}
              placeholder={t('enterUserPhone') || 'Telefon girin'}
              disabled={isLoading}
            />
            {errors.telefon && (
              <div className="invalid-feedback d-block">
                {errors.telefon}
              </div>
            )}
          </CCol>
        </CRow>

        {/* Status alanı sadece edit modunda gösterilir */}
        {isEdit && (
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="status">
                {t('userStatus') || 'Durum'}
              </CFormLabel>
              <CFormSelect
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                invalid={!!errors.status}
                disabled={isLoading}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CFormSelect>
              {errors.status && (
                <div className="invalid-feedback d-block">
                  {errors.status}
                </div>
              )}
            </CCol>
          </CRow>
        )}

        <CRow>
          <CCol md={12} className="d-flex gap-2 mt-3">
            <CButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>

            <CButton
              color="secondary"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <CIcon icon={cilX} className="me-2" />
              {t('cancel') || 'İptal'}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

UsersInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default UsersInputs;